import { clone, recordPatches } from 'mobx-state-tree';
import { ORDER_TYPES } from '@nandosaus/constants';
import { get, isNil } from 'lodash';
import { observer, useLocalStore } from 'mobx-react';
import { PropTypes } from 'prop-types';
import { useRef, useState, useEffect } from 'react';
import { useRootStore } from '@nandosaus/state-management';

import { useCurrentLocation } from '@hooks/use-current-location';
import { P } from '../typography/new';
import { AddressAutocomplete } from '../google-places-autocomplete';
import { Box, Flex } from '../grid';
import { Button } from '../button/new';
import { DeliveryError } from './delivery-error';
import { DeliveryEstimate } from './delivery-estimate';
import { getDeliveryLocationStatus } from './get-delivery-location-status';
import { Icon } from '../icons';
import { LoadingIndicator } from '../icons/loading-indicator';
import { Modal } from '../modal/new';
import { TextInput } from '../input';
import { useFeature } from '../../hooks/use-feature';
import { ValidateStreetNumber } from './validate-street-number';
import { LocationDistanceAlert } from '../location-distance-alert';

const MODAL_SCREENS = {
  DEFAULT: 'addressSearch',
  ADDRESS_CONFIRMATION: 'addressConfirm',
  STREET_NUMBER_CONFIRMATION: 'streetNumberConfirm',
};

const DeliveryAddressModal = ({ onCancel, onClose, onConfirm, renderHeader, orderType }) => {
  // Catering delivery is a scheduled advance order, so it suppresses the ASAP-only
  // affordances of normal delivery: the "you seem far away" distance alert and the
  // live ETA estimate (the delivery fee is shown as "Calculated at checkout" later).
  const isCateringDelivery = orderType === ORDER_TYPES.CATERING_DELIVERY;
  const [modalScreen, setModalScreen] = useState(MODAL_SCREENS.DEFAULT);
  const [coordinates, setCoordinates] = useState({ latitude: null, longitude: null });
  const [customUnit, setCustomUnit] = useState('');
  const customStreetNumber = useRef('');
  const { MemberStore } = useRootStore();

  const RootStore = useRootStore();

  const { setCurrentLocation } = useCurrentLocation({ shouldUseDefaultCoordinates: false });

  // on component mount, ask user if they want to use their current location
  useEffect(() => {
    setCurrentLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cloning the store allows us to work on it locally and calculate delivery
  // estimates without needing to commit changes. Changes are then committed
  // when the user presses the confirm button.
  const localStore = useLocalStore(() => clone(RootStore));
  const patchRecorder = useRef(recordPatches(localStore));

  const { DeliveryStore, CartStore, OrderingContextStore } = localStore;

  const {
    deliverabilityStatus,
    deliveryAddress,
    deliveryNotes,
    deliveryRestaurant,
    getDeliveryEstimate,
    previousSearches,
    setDeliveryAddress,
    setDeliveryStreet,
    setDeliveryNotes,
    setDeliveryUnit,
    shortestDeliveryTime,
  } = DeliveryStore;

  const loading = DeliveryStore.loading || CartStore.loading;

  const savedAddressEnabled = useFeature('save-order-addresses-and-restaurants');

  const savedAddresses = savedAddressEnabled ? get(MemberStore, 'profile.addresses', []) : previousSearches;

  const setCustomStreetNumber = streetNumber => {
    customStreetNumber.current = streetNumber;
  };

  const saveChanges = () => {
    if (deliveryAddress.unit !== customUnit) {
      setDeliveryUnit(customUnit);
    }

    patchRecorder.current.replay(RootStore);

    setModalScreen(MODAL_SCREENS.DEFAULT);
    onConfirm();
  };

  const saveStreetNumber = ({ streetNumber }) => {
    setDeliveryStreet(`${streetNumber} ${deliveryAddress.street}`);
    OrderingContextStore.updateSettings({ showMap: false });
    setModalScreen(MODAL_SCREENS.ADDRESS_CONFIRMATION);
  };

  const handleAddressSelection = ({
    address: { latitude, longitude, street, suburb, state, postcode, unit },
    saveToPreviousSearch,
  }) => {
    setDeliveryAddress(
      {
        street,
        suburb,
        postcode,
        state,
        longitude,
        latitude,
        unit,
      },
      { saveToPreviousSearch }
    );
    console.log('longitude, latitude from handle adress', longitude, latitude);
    setCoordinates({ latitude, longitude });

    if (!isNil(unit)) {
      setCustomUnit(unit);
    }
  };

  if (modalScreen === MODAL_SCREENS.DEFAULT) {
    return (
      <Modal
        header={
          renderHeader
            ? renderHeader({ handleBack: onCancel, handleClose: onClose, title: 'Delivery Address' })
            : undefined
        }
        handleBack={onCancel}
        handleClose={onClose}
        modalTitle="Delivery Address"
        open
        hideBottomDecal={false}
        isHiding
        variant="side"
      >
        <Box minHeight={{ md: 520 }}>
          <AddressAutocomplete
            countryCode={process.env.locationAutocomplete.countryCode}
            handleAddressSelection={handleAddressSelection}
            predefinedPlaces={savedAddresses.map(previousSearch => ({
              description: previousSearch.formattedAddress,
              address: previousSearch,
            }))}
            handleConfirmStreetAddress={streetNumber => {
              setCustomStreetNumber(streetNumber);
              setModalScreen(MODAL_SCREENS.STREET_NUMBER_CONFIRMATION);
            }}
            showConfirmationModal={() => setModalScreen(MODAL_SCREENS.ADDRESS_CONFIRMATION)}
          />
        </Box>
      </Modal>
    );
  }

  const { isValid: validDeliveryLocation, errorStatus: deliveryErrorStatus } = getDeliveryLocationStatus({
    deliverabilityStatus,
    deliveryRestaurant,
    isCateringDelivery,
  });

  if (modalScreen === MODAL_SCREENS.STREET_NUMBER_CONFIRMATION && validDeliveryLocation) {
    return (
      <ValidateStreetNumber
        saveStreetNumber={saveStreetNumber}
        handleBack={() => setModalScreen(MODAL_SCREENS.DEFAULT)}
        handleClose={onClose}
        address={deliveryAddress.formattedAddress}
        streetNumber={customStreetNumber.current}
        renderHeader={renderHeader}
      />
    );
  }

  const reselectAddress = () => {
    setModalScreen(MODAL_SCREENS.DEFAULT);
    setCustomUnit('');
  };

  return (
    <Modal
      header={
        renderHeader
          ? renderHeader({ handleBack: reselectAddress, handleClose: onClose, title: 'Delivery Address' })
          : undefined
      }
      handleBack={reselectAddress}
      handleClose={onClose}
      modalTitle="Delivery Address"
      variant="side"
      hideBottomDecal={false}
      open
      buttons={{
        primary: (
          <Button
            size="tall"
            isFullWidth
            widthType="fullWidth"
            disabled={loading || shortestDeliveryTime === null || !validDeliveryLocation}
            onClick={saveChanges}
          >
            Save and continue
          </Button>
        ),
      }}
    >
      <Flex flexDirection="column" gap={{ xs: '24px', md: '32px' }} minHeight={{ md: 520 }} height="100%">
        <Flex flexDirection="column" gap="8px" width="100%">
          <Flex background="white" width="100%" p="16px" borderRadius="8px" gap="8px">
            <Icon name="locationMarker" fill="black" />
            <Flex flexDirection="column" flex="1">
              <P variant="standard-semi-bold" textDecoration="none" color="black">
                {deliveryAddress.formattedStreet}
              </P>
              <P as="span" textDecoration="none" variant="extra-small" mt="4px">
                {deliveryAddress.formattedSuburbStatePostcode}
              </P>
            </Flex>

            <Flex justifyContent="flex-end">
              <P variant="extra-small" textDecoration="underline" onClick={reselectAddress}>
                Change
              </P>
            </Flex>
          </Flex>
          {!isCateringDelivery && (
            <LocationDistanceAlert
              orderType={ORDER_TYPES.DELIVERY}
              latitude={coordinates.latitude}
              longitude={coordinates.longitude}
            />
          )}
          {!loading && !validDeliveryLocation && (
            <DeliveryError
              isCateringDelivery={isCateringDelivery}
              onChangeAddressClick={reselectAddress}
              onChangeOrderClick={onCancel}
              onRetryClick={getDeliveryEstimate}
              status={deliveryErrorStatus}
            />
          )}
        </Flex>
        {loading && (
          <Flex flex={1} flexDirection="column" alignItems="center" justifyContent="center">
            <LoadingIndicator />
            {DeliveryStore.loading && (
              <P variant={3} fontWeight="bold" textAlign="center" mt="0.5rem">
                Finding your closest restaurant
              </P>
            )}
            {CartStore.loading && (
              <P variant={3} fontWeight="bold" textAlign="center" mt="0.5rem">
                Validating
              </P>
            )}
          </Flex>
        )}
        {!loading && validDeliveryLocation && (
          <>
            <Box>
              <TextInput
                label="Unit/Flat/Building etc."
                name="unit"
                value={customUnit}
                onChange={event => setCustomUnit(event.target.value)}
              />
            </Box>
            {isCateringDelivery ? (
              deliveryRestaurant?.name && (
                <Box>
                  <P variant="small-semi-bold" mb="12px">
                    Delivery from
                  </P>
                  <Flex gap="8px" alignItems="center">
                    <Icon name="barci" height="20px" width="20px" />
                    <P variant="small">{deliveryRestaurant.name}</P>
                  </Flex>
                </Box>
              )
            ) : (
              <DeliveryEstimate handleChangeOrderType={onCancel} estimate={shortestDeliveryTime} />
            )}
            <Box>
              <TextInput
                name="notes"
                label="Delivery instructions"
                value={deliveryNotes || ''}
                onChange={event => setDeliveryNotes(event.target.value)}
                placeholder="Add instructions to help with delivery"
              />
            </Box>
            <Flex flexDirection="column">
              <P variant="small-semi-bold" mb="12px">
                Good news.
              </P>
              <P variant="small">
                You can now earn and burn points on delivery orders plus redeem all your favourite Nando’s offers
                through our delivery channel.
              </P>
            </Flex>
          </>
        )}
      </Flex>
    </Modal>
  );
};

DeliveryAddressModal.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  renderHeader: PropTypes.func,
  orderType: PropTypes.string,
};

DeliveryAddressModal.defaultProps = {
  renderHeader: null,
  orderType: ORDER_TYPES.DELIVERY,
};

const ComposedDeliveryAddressModal = observer(DeliveryAddressModal);
export { ComposedDeliveryAddressModal as DeliveryAddressModal };
